import { __spreadArrays } from "tslib";
import { isDate, isValidDate, Platform } from '@tyler-components-web/core';
import { CalendarDateSelectType, CalendarView } from '../calendar';
import { formatDate, parseDateString } from '../core/date-utils';
import { DEFAULT_DATE_MASK } from '../core/mask/date-input-mask';
import { DATEPICKER_CONSTANTS } from './datepicker-constants';
var DatepickerFoundation = /** @class */ (function () {
    function DatepickerFoundation(_adapter) {
        var _this = this;
        this._adapter = _adapter;
        this._value = null;
        this._min = null;
        this._max = null;
        this._blockedDates = null;
        this._open = false;
        this._disabled = false;
        this._masked = false;
        this._maskFormat = DEFAULT_DATE_MASK;
        this._showMaskFormat = false;
        this._useCharMask = false;
        this._valueMode = 'object';
        this._notifyInputValueChanges = true;
        this._allowInvalidDate = false;
        this._isInitialized = false;
        this._showToday = false;
        this._showClear = false;
        this._startView = CalendarView.Date;
        this._inputListener = function (evt) { return _this._onInput(evt); };
        this._inputKeydownListener = function (evt) { return _this._onInputKeydown(evt); };
        this._inputFocusListener = function (evt) { return _this._onInputFocus(evt); };
        this._inputBlurListener = function (evt) { return _this._onInputBlur(evt); };
        this._inputValueChangedListener = function (value) { return _this._onInputValueChanged(value); };
        this._toggleMousedownListener = function (evt) { return _this._onToggleMousedown(evt); };
        this._dropdownCloseListener = function () { return _this._onDropdownClose(); };
        this._dateSelectListener = function (value) { return _this._onDateSelected(value); };
        this._activeChangeListener = function (id) { return _this._onActiveDescendantChanged(id); };
        this._todayListener = function () { return _this._onToday(); };
        this._clearListener = function () { return _this._onClear(); };
    }
    DatepickerFoundation.prototype.initialize = function () {
        this._adapter.initialize();
        this._adapter.initializeAccessibility();
        if (!this._adapter.hasInputElement()) {
            throw new Error('Unable to locate <input> element to attach to.');
        }
        if (!this._value) {
            this._value = this._coerceDateValue(this._adapter.getInputValue());
        }
        this._adapter.tryCreateToggle();
        if (!this._masked) {
            this._adapter.addInputListener('input', this._inputListener);
        }
        this._adapter.addInputListener('keydown', this._inputKeydownListener);
        this._adapter.addInputListener('focus', this._inputFocusListener);
        this._adapter.addInputListener('blur', this._inputBlurListener);
        this._adapter.addToggleListener('mousedown', this._toggleMousedownListener);
        // Suppress notification events during initialization
        this._setFormattedInputValue(true);
        this._applyDisabled();
        this._applyMask();
        this._adapter.setInputValueChangedListener(this, this._inputValueChangedListener);
        this._isInitialized = true;
    };
    DatepickerFoundation.prototype.disconnect = function () {
        this._isInitialized = false;
        this._adapter.removeInputListener('input', this._inputListener);
        this._adapter.removeInputListener('keydown', this._inputKeydownListener);
        this._adapter.removeInputListener('focus', this._inputFocusListener);
        this._adapter.removeInputListener('blur', this._inputBlurListener);
        this._adapter.removeToggleListener('mousedown', this._toggleMousedownListener);
        if (this._masked) {
            this._adapter.destroyMask();
        }
        this._closeCalendar();
        this._adapter.destroy();
    };
    DatepickerFoundation.prototype._applyDisabled = function () {
        this._adapter.setDisabled(this._disabled);
    };
    DatepickerFoundation.prototype._applyMask = function () {
        var _this = this;
        if (this._masked) {
            var options = {
                showMaskFormat: this._showMaskFormat,
                pattern: this._maskFormat,
                onChange: function (value) { return _this._handleInput(value); }
            };
            if (this._prepareMaskCallback) {
                options.prepareCallback = function (value, masked, flags, maskInstance) {
                    return _this._prepareMaskCallback.call(null, value, masked, flags, maskInstance);
                };
            }
            this._adapter.initializeMask(options);
        }
        else {
            this._adapter.destroyMask();
            this._formatInputValue();
        }
    };
    DatepickerFoundation.prototype._isSameDate = function (first, second) {
        if (!first && !second) {
            return true;
        }
        else if (!first || !second) {
            return false;
        }
        else if (!isValidDate(first) || !isValidDate(second)) {
            return false;
        }
        return first.setHours(0, 0, 0, 0) === second.setHours(0, 0, 0, 0);
    };
    DatepickerFoundation.prototype._onInput = function (evt) {
        this._handleInput(this._adapter.getInputValue());
    };
    DatepickerFoundation.prototype._handleInput = function (value) {
        var sanitizedValue = this._getSanitizedDateString(value);
        var date = this._coerceDateValue(sanitizedValue);
        if (this._masked) {
            this._adapter.emitInputEvent(DATEPICKER_CONSTANTS.events.INPUT, sanitizedValue);
        }
        if (!this._isSameDate(date, this._value) && this._isDateValueAcceptable(date)) {
            this._emitChangeEvent(date);
        }
    };
    DatepickerFoundation.prototype._onInputKeydown = function (evt) {
        // Handle keyboard shortcuts that make use of the shift key first
        if (evt.shiftKey) {
            switch (evt.code) {
                case 'Delete':
                case 'Backspace':
                    evt.preventDefault();
                    this._onClear();
                    if (this._open) {
                        this._closeCalendar(true);
                    }
                    return;
                case 'ArrowLeft':
                case 'ArrowRight':
                case 'KeyM':
                case 'KeyY':
                case 'KeyD':
                    if (this._open) {
                        evt.preventDefault();
                        this._adapter.propagateCalendarKey(evt.code, true);
                        return;
                    }
                    break;
            }
        }
        switch (evt.code) {
            case 'Esc':
            case 'Escape':
                evt.preventDefault();
                if (this._open) {
                    this._closeCalendar(true);
                }
                break;
            case 'Down':
            case 'ArrowDown':
                evt.preventDefault();
                if (!this._open) {
                    this._openCalendar(true);
                }
                else {
                    this._adapter.propagateCalendarKey(evt.code);
                }
                break;
            case 'Up':
            case 'ArrowUp':
                if (this._open) {
                    evt.preventDefault();
                    this._adapter.propagateCalendarKey(evt.code);
                }
                break;
            case 'Left':
            case 'Right':
            case 'ArrowLeft':
            case 'ArrowRight':
            case 'Enter':
            case 'Home':
            case 'End':
                if (this._open) {
                    evt.preventDefault();
                    // If we have an input mask applied, we want to make sure it doesn't receive the key
                    // events for left and right keys
                    var isLeftRight = ['ArrowLeft', 'Left', 'ArrowRight', 'Right'].includes(evt.code);
                    if (this._masked && isLeftRight) {
                        evt.stopImmediatePropagation();
                    }
                    this._adapter.propagateCalendarKey(evt.code);
                }
                break;
            case 'KeyN':
            case 'KeyT':
                evt.preventDefault();
                this._onToday();
                if (this._open) {
                    this._closeCalendar(true);
                }
                break;
        }
    };
    DatepickerFoundation.prototype._onInputFocus = function (evt) {
        this._adapter.selectInputText();
    };
    DatepickerFoundation.prototype._onInputBlur = function (evt) {
        this._formatInputValue();
        if (this._open && !this._adapter.isInputFocused()) {
            this._closeCalendar(true);
        }
    };
    DatepickerFoundation.prototype._onInputValueChanged = function (value) {
        var sanitizedValue = this._getSanitizedDateString(value);
        var date = this._coerceDateValue(sanitizedValue);
        if (!this._isSameDate(date, this._value) && this._isDateValueAcceptable(date)) {
            this.value = date;
            this._emitChangeEvent(this._value);
        }
    };
    DatepickerFoundation.prototype._getSanitizedDateString = function (value) {
        return value.replace(/_|[a-z]|[A-Z]/g, '').replace(/\/$|\/\/$/, '');
    };
    DatepickerFoundation.prototype._onToggleMousedown = function (evt) {
        var _this = this;
        if (this._disabled) {
            return;
        }
        evt.stopPropagation();
        if (this._adapter.isInputDisabled()) {
            return;
        }
        evt.preventDefault();
        if (this._open) {
            this._closeCalendar(true);
        }
        else {
            if (!Platform.isMobile) {
                this._adapter.tryFocusInput();
            }
            else {
                this._adapter.tryBlurInput();
            }
            if (Platform.TRIDENT) {
                window.requestAnimationFrame(function () { return _this._openCalendar(true); });
            }
            else {
                this._openCalendar(true);
            }
        }
    };
    DatepickerFoundation.prototype._emitChangeEvent = function (value, force) {
        if (force === void 0) { force = false; }
        var typedValue = this._getTypedValue(value);
        var wasCancelled = !this._adapter.emitHostEvent(DATEPICKER_CONSTANTS.events.CHANGE, typedValue, true, !force);
        if (!wasCancelled) {
            this._setValue(value);
            return true;
        }
        return false;
    };
    DatepickerFoundation.prototype._onDropdownClose = function () {
        this._closeCalendar(true);
    };
    DatepickerFoundation.prototype._onDateSelected = function (event) {
        var value = event.date;
        if (event.type === CalendarDateSelectType.date) {
            this._closeCalendar(true);
        }
        if (!this._emitChangeEvent(value)) {
            return;
        }
        var formattedValue = this._formatDate(value);
        this._adapter.setInputValue(formattedValue, this._notifyInputValueChanges);
        this._formatInputValue();
        if (!Platform.isMobile) {
            this._adapter.selectInputText();
        }
    };
    DatepickerFoundation.prototype._onActiveDescendantChanged = function (id) {
        this._adapter.setActiveDescendant(id);
    };
    DatepickerFoundation.prototype._onToday = function () {
        this._onDateSelected({ date: new Date(), type: CalendarDateSelectType.date, isSelected: true });
    };
    DatepickerFoundation.prototype._onClear = function () {
        this._onDateSelected({ date: null, type: CalendarDateSelectType.date, isSelected: true });
    };
    DatepickerFoundation.prototype._openCalendar = function (emitOpenEvent) {
        if (emitOpenEvent === void 0) { emitOpenEvent = false; }
        this._formatInputValue();
        this._open = true;
        this._adapter.setHostAttribute(DATEPICKER_CONSTANTS.attributes.OPEN, '');
        var calendarConfig = {
            value: this._value,
            min: this._min,
            max: this._max,
            blockedDates: this._blockedDates,
            popupClasses: this._popupClasses,
            dialogWidthThreshold: this._dialogWidthThreshold,
            closeCallback: this._dropdownCloseListener,
            selectCallback: this._dateSelectListener,
            activeChangeCallback: this._activeChangeListener,
            showToday: this._showToday,
            showClear: this._showClear,
            todayCallback: this._todayListener,
            clearCallback: this._clearListener,
            disabledDaysOfWeek: this._disabledDaysOfWeek || null,
            disableDayCallback: this._disableDayCallback,
            startView: this._startView
        };
        this._adapter.attachCalendar(calendarConfig);
        if (emitOpenEvent) {
            this._adapter.emitHostEvent(DATEPICKER_CONSTANTS.events.OPEN, true, false);
        }
    };
    DatepickerFoundation.prototype._closeCalendar = function (emitCloseEvent) {
        if (emitCloseEvent === void 0) { emitCloseEvent = false; }
        this._open = false;
        this._adapter.removeHostAttribute(DATEPICKER_CONSTANTS.attributes.OPEN);
        this._adapter.detachCalendar();
        if (emitCloseEvent) {
            this._adapter.emitHostEvent(DATEPICKER_CONSTANTS.events.CLOSE, true, false);
        }
    };
    DatepickerFoundation.prototype._formatInputValue = function () {
        var inputValue = this._adapter.getInputValue();
        if (inputValue) {
            var parsedDate = this._parseDateString(inputValue);
            if (isValidDate(parsedDate) && this._isDateValueAcceptable(parsedDate)) {
                var formattedDate = this._formatDate(parsedDate);
                if (formattedDate && formattedDate !== inputValue) {
                    this._adapter.setInputValue(formattedDate, this._notifyInputValueChanges);
                }
            }
            else {
                if (!this._allowInvalidDate) {
                    this._adapter.setInputValue('', this._notifyInputValueChanges);
                }
            }
        }
    };
    DatepickerFoundation.prototype._setFormattedInputValue = function (suppressValueChanges) {
        var formattedDate = this._formatDate(this._value);
        if (formattedDate) {
            this._adapter.setInputValue(formattedDate, suppressValueChanges ? false : this._notifyInputValueChanges);
        }
        else {
            if (!this._allowInvalidDate) {
                this._adapter.setInputValue('', suppressValueChanges ? false : this._notifyInputValueChanges);
            }
        }
    };
    DatepickerFoundation.prototype._formatDate = function (date) {
        if (!isValidDate(date)) {
            return '';
        }
        return typeof this._formatCallback === 'function' ? this._formatCallback(date) : formatDate(date);
    };
    DatepickerFoundation.prototype._parseDateString = function (value) {
        value = value.replace(/_|\s/g, '');
        if (!value || !value.length) {
            return null;
        }
        var parsedDate = typeof this._parseCallback === 'function' ? this._parseCallback(value) : parseDateString(value);
        return isValidDate(parsedDate) ? parsedDate : null;
    };
    DatepickerFoundation.prototype._coerceDateValue = function (value) {
        if (typeof value === 'string') {
            return this._parseDateString(value);
        }
        return value;
    };
    DatepickerFoundation.prototype._getTypedValue = function (value) {
        switch (this._valueMode) {
            case 'object':
                return value;
            case 'string':
                return this._formatDate(value);
            case 'iso-string':
                if (value) {
                    value.setHours(0, 0, 0, 0); // Remove the time portion of the date
                }
                return value ? value.toISOString() : null;
            default:
                return value;
        }
    };
    DatepickerFoundation.prototype._isDateValueAcceptable = function (value) {
        if (!value) {
            return true;
        }
        var passesMinDate = this._min ? this._min.getTime() <= value.getTime() : true;
        var passesMaxDate = this._max ? this._max.getTime() >= value.getTime() : true;
        var blockedDates = Array.isArray(this._blockedDates) ? this._blockedDates : this._blockedDates ? [this._blockedDates] : [];
        var isNotBlocked = !blockedDates.some(function (bd) { return bd.getTime() === value.getTime(); });
        var dayNotDisabled = !(this._disabledDaysOfWeek && this._disabledDaysOfWeek.includes(value.getDay()));
        var dateNotDisabledThroughCallback = typeof this._disableDayCallback !== 'function' || !this._disableDayCallback(value);
        return passesMinDate && passesMaxDate && isNotBlocked && dayNotDisabled && dateNotDisabledThroughCallback;
    };
    DatepickerFoundation.prototype._setValue = function (value) {
        if (!value || this._isDateValueAcceptable(value)) {
            this._value = value;
        }
    };
    Object.defineProperty(DatepickerFoundation.prototype, "value", {
        get: function () {
            var date = this._getTypedValue(this._value);
            if (!date) {
                return null;
            }
            if (isDate(date)) {
                return new Date(date.getTime());
            }
            return date;
        },
        set: function (value) {
            if (this._value !== value) {
                this._setValue(this._coerceDateValue(value));
                if (this._isInitialized) {
                    this._setFormattedInputValue();
                    if (this._open) {
                        this._adapter.setCalendarValue(this._value);
                    }
                }
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(DatepickerFoundation.prototype, "min", {
        get: function () {
            if (!this._min) {
                return null;
            }
            return new Date(this._min.getTime());
        },
        set: function (value) {
            if (this._min !== value) {
                var date = this._coerceDateValue(value);
                this._min = !!date ? new Date(date.getTime()) : null;
                if (this._value && !this._isDateValueAcceptable(this._value)) {
                    this._emitChangeEvent(null, true);
                    this._setFormattedInputValue();
                }
                if (this._isInitialized) {
                    this._adapter.setCalendarMinDate(this._min);
                }
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(DatepickerFoundation.prototype, "max", {
        get: function () {
            if (!this._max) {
                return null;
            }
            return new Date(this._max.getTime());
        },
        set: function (value) {
            if (this._max !== value) {
                this._max = this._coerceDateValue(value);
                if (this._value && !this._isDateValueAcceptable(this._value)) {
                    this._emitChangeEvent(null, true);
                    this._setFormattedInputValue();
                }
                if (this._isInitialized) {
                    this._adapter.setCalendarMaxDate(this._max);
                }
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(DatepickerFoundation.prototype, "blockedDates", {
        get: function () {
            if (!this._blockedDates) {
                return null;
            }
            if (Array.isArray(this._blockedDates)) {
                return this._blockedDates.map(function (d) { return new Date(d.getTime()); });
            }
            return new Date(this._blockedDates.getTime());
        },
        set: function (value) {
            if (!value) {
                this._blockedDates = null;
            }
            else if (Array.isArray(value)) {
                this._blockedDates = value.map(function (d) { return new Date(d.getTime()); });
            }
            else {
                this._blockedDates = new Date(value.getTime());
            }
            if (this._isInitialized && this._open) {
                this._adapter.setCalendarBlockedDates(this._blockedDates);
            }
            if (this._value && !this._isDateValueAcceptable(this._value)) {
                this._emitChangeEvent(null, true);
                this._setFormattedInputValue();
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(DatepickerFoundation.prototype, "open", {
        get: function () {
            return this._open;
        },
        set: function (value) {
            if (this._open !== value) {
                if (this._isInitialized) {
                    this._open = value;
                    if (this._open) {
                        this._openCalendar();
                    }
                    else {
                        this._closeCalendar();
                    }
                }
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(DatepickerFoundation.prototype, "disabled", {
        get: function () {
            return this._disabled;
        },
        set: function (value) {
            if (this._disabled !== value) {
                this._disabled = value;
                if (this._isInitialized) {
                    this._applyDisabled();
                }
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(DatepickerFoundation.prototype, "popupClasses", {
        get: function () {
            if (Array.isArray(this._popupClasses)) {
                return __spreadArrays(this._popupClasses);
            }
            return this._popupClasses;
        },
        set: function (value) {
            if (Array.isArray(value)) {
                this._popupClasses = __spreadArrays(value);
            }
            else {
                this._popupClasses = value;
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(DatepickerFoundation.prototype, "masked", {
        get: function () {
            return this._masked;
        },
        set: function (value) {
            if (this._masked !== value) {
                this._masked = value;
                if (this._isInitialized) {
                    if (this._masked) {
                        this._adapter.removeInputListener('input', this._inputListener);
                        this._applyMask();
                    }
                    else {
                        this._adapter.addInputListener('input', this._inputListener);
                        this._adapter.destroyMask();
                        this._formatInputValue();
                    }
                }
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(DatepickerFoundation.prototype, "maskFormat", {
        get: function () {
            return this._maskFormat;
        },
        set: function (value) {
            if (this._maskFormat !== value) {
                this._maskFormat = value;
                if (this._isInitialized) {
                    this._applyMask();
                }
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(DatepickerFoundation.prototype, "useCharMask", {
        get: function () {
            return this._useCharMask;
        },
        set: function (value) {
            if (this._useCharMask !== value) {
                this._useCharMask = value;
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(DatepickerFoundation.prototype, "showMaskFormat", {
        get: function () {
            return this._showMaskFormat;
        },
        set: function (value) {
            if (this._showMaskFormat !== value) {
                this._showMaskFormat = value;
                if (this._isInitialized) {
                    this._applyMask();
                }
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(DatepickerFoundation.prototype, "valueMode", {
        get: function () {
            return this._valueMode;
        },
        set: function (value) {
            if (this._valueMode !== value) {
                var originalValueMode = this._valueMode;
                var isValidMode = ['object', 'string', 'iso-string'].includes(value);
                this._valueMode = isValidMode ? value : 'object';
                if (originalValueMode !== this._valueMode) {
                    this._emitChangeEvent(this._value);
                    this._adapter.setHostAttribute(DATEPICKER_CONSTANTS.attributes.VALUE_MODE, this._valueMode);
                }
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(DatepickerFoundation.prototype, "dialogWidthThreshold", {
        set: function (value) {
            this._dialogWidthThreshold = value;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(DatepickerFoundation.prototype, "notifyInputValueChanges", {
        get: function () {
            return this._notifyInputValueChanges;
        },
        set: function (value) {
            this._notifyInputValueChanges = value;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(DatepickerFoundation.prototype, "allowInvalidDate", {
        get: function () {
            return this._allowInvalidDate;
        },
        set: function (value) {
            if (this._allowInvalidDate !== value) {
                this._allowInvalidDate = value;
                if (this._allowInvalidDate) {
                    this._adapter.setHostAttribute(DATEPICKER_CONSTANTS.attributes.ALLOW_INVALID_DATE);
                }
                else {
                    this._adapter.removeHostAttribute(DATEPICKER_CONSTANTS.attributes.ALLOW_INVALID_DATE);
                }
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(DatepickerFoundation.prototype, "showToday", {
        get: function () {
            return this._showToday;
        },
        set: function (value) {
            this._showToday = value;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(DatepickerFoundation.prototype, "showClear", {
        get: function () {
            return this._showClear;
        },
        set: function (value) {
            this._showClear = value;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(DatepickerFoundation.prototype, "disabledDaysOfWeek", {
        get: function () {
            return this._disabledDaysOfWeek;
        },
        set: function (value) {
            if (value !== this._disabledDaysOfWeek) {
                this._disabledDaysOfWeek = value;
                this.applyDisabledDaysOfWeek();
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(DatepickerFoundation.prototype, "disableDayCallback", {
        set: function (value) {
            if (value !== this._disableDayCallback) {
                this._disableDayCallback = value;
                this._applyDisableDayCallback();
            }
        },
        enumerable: true,
        configurable: true
    });
    DatepickerFoundation.prototype._applyDisableDayCallback = function () {
        this._adapter.setDisableDayCallback(this._disableDayCallback);
    };
    DatepickerFoundation.prototype.applyDisabledDaysOfWeek = function () {
        if (this._isInitialized && this._open) {
            this._adapter.setCalendarDisabledDaysOfWeek(this._disabledDaysOfWeek);
        }
        if (this._value && !this._isDateValueAcceptable(this._value)) {
            this._emitChangeEvent(null, true);
            this._setFormattedInputValue();
        }
    };
    Object.defineProperty(DatepickerFoundation.prototype, "parseCallback", {
        get: function () {
            return this._parseCallback;
        },
        set: function (cb) {
            this._parseCallback = cb;
            if (this._isInitialized) {
                this._applyMask();
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(DatepickerFoundation.prototype, "formatCallback", {
        get: function () {
            return this._formatCallback;
        },
        set: function (cb) {
            this._formatCallback = cb;
            if (this._isInitialized) {
                this._applyMask();
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(DatepickerFoundation.prototype, "prepareMaskCallback", {
        get: function () {
            return this._prepareMaskCallback;
        },
        set: function (cb) {
            this._prepareMaskCallback = cb;
            if (this._isInitialized) {
                this._applyMask();
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(DatepickerFoundation.prototype, "startView", {
        get: function () {
            return this._startView;
        },
        set: function (value) {
            if (value !== this._startView) {
                this._startView = value;
            }
        },
        enumerable: true,
        configurable: true
    });
    return DatepickerFoundation;
}());
export { DatepickerFoundation };
